/**
 * Seasonal Promotion Templates
 * Holiday and seasonal campaigns for maximum engagement
 */

import { PromotionTemplate } from "./types";

export const SEASONAL_TEMPLATES: PromotionTemplate[] = [
  {
    id: "new-year-sale",
    name: "New Year Sale",
    category: "seasonal",
    description: "Ring in the new year with special pricing to drive Q1 subscriptions",
    strategy: "Leverage the 'fresh start' mindset of January. Users are motivated to invest in productivity tools as part of their New Year resolutions. Create urgency with a countdown to January deadline.",
    icon: "CalendarOutlined",
    color: "#8B5CF6",
    expectedOutcomes: [
      "20-40% conversion lift during campaign",
      "Strong Q1 revenue boost",
      "Capture resolution-minded users",
    ],
    bestPractices: [
      "Start promotion Dec 26th for post-holiday momentum",
      "End by Jan 7th to maintain urgency",
      "Use countdown timer to end date",
      "Highlight 'fresh start' messaging",
    ],
    tags: ["seasonal", "new-year", "q1", "resolution"],
    bestFor: ["B2C SaaS", "Productivity tools", "Self-improvement apps"],
    difficulty: "beginner",
    setupTime: "15 min",
    defaults: {
      type: "popup",
      displayStyle: "festive",
      priority: 9,
      targetAudience: ["free", "trial"],
      discount: {
        discountType: "percentage",
        discountValue: 50,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "gradient_purple",
        primaryColor: "#8B5CF6",
        secondaryColor: "#06B6D4",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard", "pricing"],
        maxImpressionsPerUser: 5,
        delaySeconds: 3,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "New Year, New You!",
          subheadline: "Start 2025 with unlimited AI power. Get 50% off your first year.",
          ctaText: "Claim My Discount",
          ctaSecondaryText: "Learn More",
          badge: "LIMITED TIME",
          terms: "*Offer valid until January 7th. New subscribers only.",
        },
      },
    },
  },
  {
    id: "black-friday",
    name: "Black Friday Deal",
    category: "seasonal",
    description: "The biggest sale of the year - maximize conversions with aggressive discounts",
    strategy: "Black Friday is the most anticipated sale event. Users expect deep discounts and are primed to buy. Use strong urgency tactics and make your offer impossible to ignore.",
    icon: "ThunderboltOutlined",
    color: "#1F2937",
    expectedOutcomes: [
      "3-5x normal conversion rate",
      "Highest revenue day of the year",
      "Large influx of annual subscribers",
    ],
    bestPractices: [
      "Offer your deepest discount of the year",
      "Start Thursday evening for early birds",
      "Extend through Cyber Monday",
      "Use countdown timers aggressively",
    ],
    tags: ["seasonal", "black-friday", "cyber-monday", "q4"],
    bestFor: ["All SaaS products", "Consumer apps", "B2B tools"],
    difficulty: "beginner",
    setupTime: "20 min",
    defaults: {
      type: "popup",
      displayStyle: "bold",
      priority: 10,
      targetAudience: ["free", "trial", "churned"],
      discount: {
        discountType: "percentage",
        discountValue: 70,
        currency: "USD",
        applicablePlans: ["pro", "starter", "enterprise"],
      },
      styling: {
        theme: "dark",
        primaryColor: "#F59E0B",
        secondaryColor: "#1F2937",
        animation: "pulse",
      },
      displayConditions: {
        showOnPages: ["all"],
        maxImpressionsPerUser: 10,
        delaySeconds: 0,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 100,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "BLACK FRIDAY BLOWOUT",
          subheadline: "Our biggest sale EVER. 70% off all plans - this weekend only!",
          ctaText: "Get 70% Off Now",
          ctaSecondaryText: "View Plans",
          badge: "BEST DEAL",
          terms: "*Valid Nov 24-27 only. Cannot combine with other offers.",
        },
      },
    },
  },
  {
    id: "christmas-special",
    name: "Christmas Special",
    category: "seasonal",
    description: "Spread holiday cheer with a festive promotion",
    strategy: "Christmas promotions work well for gifting and self-treats. Use warm, festive imagery and messaging. Many users have holiday downtime to explore new tools.",
    icon: "GiftOutlined",
    color: "#DC2626",
    expectedOutcomes: [
      "15-25% conversion lift",
      "Gift subscriptions increase",
      "Year-end budget spending capture",
    ],
    bestPractices: [
      "Start December 15th",
      "Emphasize gifting options",
      "Use festive colors and imagery",
      "Extend through Dec 26th",
    ],
    tags: ["seasonal", "christmas", "holiday", "december"],
    bestFor: ["Consumer apps", "Creative tools", "Gift-able products"],
    difficulty: "beginner",
    setupTime: "15 min",
    defaults: {
      type: "banner",
      displayStyle: "festive",
      priority: 8,
      targetAudience: ["free", "trial"],
      discount: {
        discountType: "percentage",
        discountValue: 40,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "holiday",
        primaryColor: "#DC2626",
        secondaryColor: "#15803D",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard", "pricing"],
        maxImpressionsPerUser: 5,
        delaySeconds: 2,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Holiday Special!",
          subheadline: "Give yourself the gift of productivity. 40% off all plans.",
          ctaText: "Unwrap My Discount",
          ctaSecondaryText: "Gift a Subscription",
          badge: "HOLIDAY OFFER",
          terms: "*Valid through December 26th.",
        },
      },
    },
  },
  {
    id: "summer-sale",
    name: "Summer Sale",
    category: "seasonal",
    description: "Beat the summer slump with a mid-year promotion",
    strategy: "Summer is typically a slower period for SaaS. Use a sale to maintain momentum and capture users who have more time to explore tools. Focus on 'summer productivity' messaging.",
    icon: "FireOutlined",
    color: "#F97316",
    expectedOutcomes: [
      "Maintain revenue during slow season",
      "10-20% conversion lift",
      "Capture students on break",
    ],
    bestPractices: [
      "Run late June through July",
      "Target students and seasonal workers",
      "Use bright, energetic colors",
      "Emphasize 'get ahead' messaging",
    ],
    tags: ["seasonal", "summer", "mid-year", "q2-q3"],
    bestFor: ["Educational tools", "Productivity apps", "Creative software"],
    difficulty: "beginner",
    setupTime: "15 min",
    defaults: {
      type: "banner",
      displayStyle: "gradient",
      priority: 6,
      targetAudience: ["free", "trial"],
      discount: {
        discountType: "percentage",
        discountValue: 30,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "gradient_blue",
        primaryColor: "#F97316",
        secondaryColor: "#FBBF24",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard", "pricing"],
        maxImpressionsPerUser: 3,
        delaySeconds: 5,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Summer Sale!",
          subheadline: "Hot deals for the summer. Save 30% on any plan.",
          ctaText: "Get Summer Savings",
          ctaSecondaryText: "See Plans",
          badge: "SUMMER DEAL",
          terms: "*Valid through July 31st.",
        },
      },
    },
  },
  {
    id: "back-to-school",
    name: "Back to School",
    category: "seasonal",
    description: "Target students and educators with academic-year pricing",
    strategy: "Back to school season is prime time for productivity tools. Students and educators are setting up their tech stack for the year. Offer education-focused discounts.",
    icon: "BookOutlined",
    color: "#3B82F6",
    expectedOutcomes: [
      "Capture student demographic",
      "Annual subscriptions for academic year",
      "Word-of-mouth in educational settings",
    ],
    bestPractices: [
      "Run August through September",
      "Verify student/educator status if possible",
      "Emphasize productivity and learning",
      "Consider longer trial periods",
    ],
    tags: ["seasonal", "education", "students", "back-to-school"],
    bestFor: ["Educational tools", "Note-taking apps", "Research tools"],
    difficulty: "intermediate",
    setupTime: "20 min",
    defaults: {
      type: "popup",
      displayStyle: "standard",
      priority: 7,
      targetAudience: ["free", "new"],
      discount: {
        discountType: "percentage",
        discountValue: 50,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "default",
        primaryColor: "#3B82F6",
        secondaryColor: "#8B5CF6",
        animation: "slide",
      },
      displayConditions: {
        showOnPages: ["dashboard", "pricing"],
        maxImpressionsPerUser: 5,
        delaySeconds: 3,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Back to School Special",
          subheadline: "Start the academic year right. 50% off for students & educators.",
          ctaText: "Get Student Discount",
          ctaSecondaryText: "Verify Status",
          badge: "STUDENTS",
          terms: "*Valid August 15 - September 30. Student verification required.",
        },
      },
    },
  },
];
